class ScrollSnapSlider extends HTMLUListElement {
  constructor() {
    super();

    this.prevButton = this.parentNode.querySelector(".slider-buttons .slider-button--prev");
    this.nextButton = this.parentNode.querySelector(".slider-buttons .slider-button--next");
    this.indicatorWrapper = this.parentNode.querySelector(".indicator-wrapper");
    this.indicators = this.parentNode.querySelectorAll(".indicator");
    this.currentIndicator = this.parentNode.querySelector(".indicator .current-indicator");
    if (!this.currentIndicator && this.indicators.length) {
      this.currentIndicator = document.createElement("span");
      this.currentIndicator.classList.add("visually-hidden", "current-indicator");
      this.indicators[0].appendChild(this.currentIndicator);
    }
    this.isAutoplay = (this.dataset.autoplay || "false") === "true";
    this.isLoop = (this.dataset.loop || "false") === "true";
    this.isDraggable = (this.dataset.draggable || "false") === "true";
    this.autoplayInterval = this.dataset.autoplayInterval ? this.dataset.autoplayInterval : 3000;

    // ✅ Read indicator limits directly from Liquid-generated markup
    this.desktopIndicatorLimit = parseInt(this.indicatorWrapper?.dataset.indicatorDesktop || this.indicators.length);
    this.mobileIndicatorLimit = parseInt(this.indicatorWrapper?.dataset.indicatorMobile || this.indicators.length);

    // ✅ Apply correct visibility on load & resize
    this.updateIndicatorVisibility();
    window.addEventListener("resize", () => this.updateIndicatorVisibility());

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  /** ✅ Hide or show indicators based on current screen width */
  updateIndicatorVisibility() {
    const isMobile = window.innerWidth <= 768;
    const visibleCount = isMobile ? this.mobileIndicatorLimit : this.desktopIndicatorLimit;

    this.indicators.forEach((indicator, index) => {
      // Only show indicators up to the visible limit
      if (index <= visibleCount) {
        indicator.style.display = "";
      } else {
        indicator.style.display = "none";
      }
    });
  }
  async init() {
    const { ScrollSnapSlider, ScrollSnapDraggable, ScrollSnapAutoplay, ScrollSnapLoop } = await import("mdl-scrollsnap");

    const sliderElement = this;
    const sliderSimple = new ScrollSnapSlider({ element: this });
    const draggablePlugin = new ScrollSnapDraggable(70);
    const autoplayPlugin = new ScrollSnapAutoplay(this.autoplayInterval);
    const loopPlugin = new ScrollSnapLoop();

    this.slider = sliderSimple;

    draggablePlugin.slider = sliderSimple;
    autoplayPlugin.slider = sliderSimple;
    loopPlugin.slider = sliderSimple;

    const hasNavigationButtons = this.prevButton && this.nextButton;

    const updateNavButtonsState = () => {
      if (!hasNavigationButtons) return;

      const scrollableWidth = sliderElement.scrollWidth - sliderElement.clientWidth;
      const tolerance = 2;

      if (scrollableWidth <= tolerance) {
        this.prevButton?.setAttribute("disabled", "disabled");
        this.nextButton?.setAttribute("disabled", "disabled");
        return;
      }

      const atStart = sliderElement.scrollLeft <= tolerance;
      const atEnd = sliderElement.scrollLeft >= scrollableWidth - tolerance;

      if (atStart) {
        this.prevButton?.setAttribute("disabled", "disabled");
      } else {
        this.prevButton?.removeAttribute("disabled");
      }

      if (atEnd) {
        this.nextButton?.setAttribute("disabled", "disabled");
      } else {
        this.nextButton?.removeAttribute("disabled");
      }
    };

    this.prevButton?.addEventListener("click", () => {
      autoplayPlugin.disableTemporarily();
      sliderSimple.slideTo(sliderSimple.slide - 1);
      requestAnimationFrame(updateNavButtonsState);
    });
    this.nextButton?.addEventListener("click", () => {
      autoplayPlugin.disableTemporarily();
      sliderSimple.slideTo(sliderSimple.slide + 1);
      requestAnimationFrame(updateNavButtonsState);
    });

    for (const indicator of this.indicators) {
      indicator.addEventListener("click", () => {
        autoplayPlugin.disableTemporarily();
        const slideElementIndex = Array.prototype.slice.call(sliderElement.children).findIndex(
          (item) => item.dataset.index === indicator.dataset.index
        );
        sliderSimple.slideTo(slideElementIndex);
      });
    }

    this.slider.addEventListener("slide-stop", (event) => {
      this.updateSlideState(event);
      this.updateIndicator(event);
      updateNavButtonsState();
    });

    if (this.isAutoplay) {
      this.enablePlugin(autoplayPlugin);
    }

    if (this.isLoop) {
      this.enablePlugin(loopPlugin);
    }

    if (this.isDraggable) {
      this.enablePlugin(draggablePlugin);
      if (this.isLoop) {
        this.disablePlugin(loopPlugin);
      }
    } else {
      this.disablePlugin(draggablePlugin);
    }

    if (hasNavigationButtons) {
      requestAnimationFrame(updateNavButtonsState);
      window.addEventListener("resize", updateNavButtonsState, { passive: true });
    }
  }

  slideTo(index) {
    this.slider.slideTo(index);
  }

  enablePlugin(plugin) {
    plugin.enable();
    this.slider.plugins.set(plugin.id, plugin);
  }

  disablePlugin(plugin) {
    plugin.disable();
    this.slider.plugins.delete(plugin.id);
  }

  updateSlideState(event) {
    const children = event.target.children;
    if (!children || event.detail < 0 || event.detail >= children.length) {
      return;
    }
    for (const child of children) {
      child.classList.remove("-active");
    }
    children[event.detail].classList.add("-active");
  }

  updateIndicator(event) {
    const slideElementIndex = event.detail;
    const slideElement = event.target.children[slideElementIndex];

    for (const indicator of this.indicators) {
      const isActive = indicator.classList.toggle(
        "-active",
        indicator.dataset.index === slideElement.dataset.index
      );

      if (isActive && this.currentIndicator) {
        indicator.appendChild(this.currentIndicator);
      }
    }
  }
}
customElements.define("scroll-snap-slider", ScrollSnapSlider, { extends: "ul" });